import { useQuery } from '@tanstack/react-query';
import { PageHeader, Badge, Card } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchPipeline } from '@/data/sources';
import { fetchPipelineSummary } from '@/data/sources-salesb';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { formatCurrency } from '@/lib/utils';
import type { Opportunity } from '@/types';
import type { PipelineSummary } from '@/types/salesb';

/* /sales/pipeline — kanban skeleton (brief v1 §4b: one configurable pipeline).
 * Columns come from the workspace's ordered stages (API) or the default six
 * (mock). Drag/drop + persistence come with the design + API pass.
 *
 * W2b enhancement: a real pipeline-summary strip sits above the board, reading
 * the server aggregate (GET /v1/crm/pipeline-summary → bc_crm_opportunities).
 * FREE + read-only, source-attributed, honest states — the per-stage count +
 * value and the total are the real backend aggregate, not a client guess. */

const SUMMARY_SOURCE_TAG = 'Source: Pipeline aggregate (bc_crm_opportunities)';

function OppCard({ opp }: { opp: Opportunity }) {
  return (
    <div className="rounded-md border border-border-subtle bg-surface p-3 shadow-sm">
      <div className="text-sm font-medium text-text-primary">{opp.name}</div>
      <div className="text-xs text-text-tertiary">{opp.company}</div>
      <div className="mt-2 flex items-center justify-between">
        <span className="font-mono text-sm font-semibold text-text-primary" data-tabular>
          {formatCurrency(opp.value, opp.currency)}
        </span>
        <span className="text-[11px] text-text-tertiary">{opp.owner}</span>
      </div>
    </div>
  );
}

/** Real server aggregate of the pipeline — per-stage count + value and the
 *  workspace total. Honest states; a stage with zero value renders £0 because
 *  that IS the aggregate, not an invented number. */
function PipelineSummaryStrip() {
  const { active } = useActiveDomain();
  // Domain-scoped aggregate: opportunities matched to the Viewed domain via
  // their originating lead's host. Keyed by projectId so a switch refetches.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['crm', 'pipeline-summary', active?.projectId ?? 'all'],
    queryFn: () => fetchPipelineSummary(active?.projectId),
  });

  if (isPending) return <LoadingState rows={2} />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const summary = data as PipelineSummary;
  const hasStages = summary.stages.length > 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge tone="neutral">{SUMMARY_SOURCE_TAG}</Badge>
        <div className="text-xs text-text-tertiary">
          Total pipeline value{' '}
          <span className="font-mono font-semibold text-text-primary" data-tabular>
            {formatCurrency(summary.total_value, summary.currency)}
          </span>
        </div>
      </div>

      {hasStages ? (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {summary.stages.map((s) => (
            <div key={s.stage} className="rounded-lg border border-border-subtle bg-surface p-3">
              <div className="text-[11px] uppercase tracking-wide text-text-tertiary">{s.stage}</div>
              <div className="mt-1 font-mono text-lg font-semibold text-text-primary" data-tabular>
                {formatCurrency(s.value, s.currency)}
              </div>
              <div className="mt-0.5 text-[11px] text-text-tertiary">
                {s.count.toLocaleString('en-GB')} {s.count === 1 ? 'opportunity' : 'opportunities'}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-text-tertiary">
          No opportunities aggregated yet — stage values appear here as you open and move
          opportunities. Read-only from {summary.source}.
        </p>
      )}
    </Card>
  );
}

export function PipelinePage() {
  const { active } = useActiveDomain();
  // Board query is keyed by the active domain so a switch invalidates +
  // refetches (no stale board). NOTE: the board reads /crm/opportunities, whose
  // per-domain backend scoping is deferred (out of this fix's owned scope);
  // the domain-scoped pipeline aggregate above is authoritative for this wave.
  const { data: columns, isPending, error, refetch } = useQuery({
    queryKey: ['crm', 'pipeline', active?.projectId ?? 'all'],
    queryFn: fetchPipeline,
  });

  const isEmpty = !!columns && columns.every((c) => c.opportunities.length === 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Pipeline" subtitle="One configurable pipeline · New → Contacted → Qualified → Proposal → Won/Lost" />

      {/* Real server aggregate — free, read-only, source-attributed. */}
      <PipelineSummaryStrip />

      {isPending ? (
        <LoadingState rows={4} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isEmpty ? (
        <EmptyState
          title="Your pipeline is ready"
          teach="Opportunities open here automatically when you promote a lead from Outreach — in the New stage, carrying the lead's value and attribution — and move stage-by-stage as you work them."
        />
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="flex min-w-[900px] gap-3">
            {columns.map((col) => (
              <div key={col.key} className="flex w-56 shrink-0 flex-col rounded-lg bg-inset/50 p-2">
                <div className="mb-2 flex items-center justify-between px-1">
                  <span className="text-xs font-semibold text-text-primary">{col.label}</span>
                  <Badge tone="neutral">
                    {formatCurrency(col.opportunities.reduce((sum, o) => sum + o.value, 0))}
                  </Badge>
                </div>
                <div className="flex flex-col gap-2">
                  {col.opportunities.map((o) => (
                    <OppCard key={o.id} opp={o} />
                  ))}
                  {col.opportunities.length === 0 && (
                    <div className="rounded-md border border-dashed border-border-default py-6 text-center text-[11px] text-text-tertiary">
                      Empty
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
